import React, { useEffect, useState } from 'react'
import CommonModal from '../../Components/CommonModal';
import { organization_domain, toggle_blacklist, toggle_popup } from '../../services';
import SceenLoader from '../../Components/ScreenLoader';
import { useNavigate } from 'react-router-dom';
import AddDomainModal from './AddDomainModal';


function WhiteListDomainModal(props) {
    const [organizationName, setOrganization] = useState([]);
    const [selectedDomains, setSelectedDomains] = useState({});
    const [screenLoader, setScreenLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showAddDomainModal, setShowAddDomainModal] = useState(false);
    const [apiError, setApiError] = useState('');

    const navigate = useNavigate();

    // Primary domain — derived from the logged-in user's email (e.g.
    // sanjay.singh@foreseesecurity.io → nmtsecurity.com). It's the org's own
    // domain, so it's shown read-only at the top and never blacklistable.
    const primaryDomain = (() => {
        const email = localStorage.getItem('email') || '';
        return email.includes('@') ? email.split('@')[1].trim().toLowerCase() : '';
    })();

    const organization_name = async () => {
        setScreenLoader(true)
        const response = await organization_domain();
        // Fall back to [] — the component reads .length in three places and a
        // null/absent payload would throw rather than render an empty list.
        setOrganization(response?.statusCode?.response_data?.data || []);
        setScreenLoader(false)
    };

    useEffect(() => {
        organization_name();
    }, []);

    const handleCheckboxChange = (id, type, isChecked) => {
        setApiError('');
        // Keyed by type + id, not id alone: domains and subdomains are separate
        // entities with independent id sequences, so a bare id can collide and
        // silently drop one of the two from the payload.
        setSelectedDomains(prev => ({
            ...prev,
            [`${type}-${id}`]: { id, type, "blacklist_status": !isChecked }
        }));
    };

    const toggleBlacklist = async () => {
        const items = Object.values(selectedDomains);
        // Nothing was toggled. The old code still posted an empty list and then
        // reloaded, which is indistinguishable from a save that worked.
        if (items.length === 0) {
            setApiError('Change at least one domain before submitting.');
            return;
        }

        setLoading(true);
        setApiError('');
        try {
            const response = await toggle_blacklist({ items });
            // ApiIntercepter resolves to a truthy { statusCode, errors } for
            // FAILURES too — a rejected save arrives as HTTP 200 with
            // status: 0 and an `error` body. The old `if (response)` was
            // therefore always true, so a failed whitelist still closed the
            // modal and called navigate(0); the reload then showed the
            // unchanged server state, which reads as the selection
            // "unselecting itself after refresh".
            const errorMessage = response?.errors?.message
                || response?.statusCode?.error?.message;
            const failed = Boolean(errorMessage) || response?.statusCode?.status === 0;

            if (!failed) {
                props.onHide();
                navigate(0);
                return;                 // page is reloading — leave state alone
            }
            setApiError(errorMessage || 'Could not update the whitelist. Please try again.');
        } catch (error) {
            setApiError(
                error?.response?.data?.error?.message
                || 'Something went wrong while updating the whitelist.'
            );
        } finally {
            // Was unreachable on a thrown request, leaving Submit stuck on
            // "Submitting…" with no way to retry but closing the modal.
            setLoading(false);
        }
    };

    const closePopup = async () => {
        const response = await toggle_popup();
        const popupClosed = response?.statusCode?.response_data?.data?.is_popup_closed
        if (popupClosed) {
            localStorage.setItem('is_popup_closed', 'true');
        }
        props.onHide();
    }
    // "Add Domain" was a header action next to an h2 that duplicated what the
    // modal header should have held — hence the empty title bar above it. The
    // heading becomes CommonModal's title and both actions move to its footer.
    const whitelistActions = screenLoader ? [] : [
        { text: 'Add Domain', variant: 'secondary', onClick: () => setShowAddDomainModal(true) },
        {
            text: 'Submit',
            variant: 'primary',
            onClick: toggleBlacklist,
            loading,
            loadingText: 'Submitting…',
            disabled: loading,
        },
    ];

    return (
        <CommonModal
            show={props?.show}
            onHide={closePopup}
            title="Co-Domain Whitelist"
            subtitle={organizationName.length ? `${organizationName.length} domain${organizationName.length === 1 ? '' : 's'}` : undefined}
            buttons={whitelistActions}
            error={apiError}
        >
            <div className="white_list_box">
                {screenLoader ? <SceenLoader /> : (
                    <ul>
                        {primaryDomain && (
                            <li className="white_list_data primary_domain_row" key="primary-domain">
                                <input
                                    type="checkbox"
                                    checked
                                    disabled
                                    readOnly
                                />
                                <span className="primary_domain_name">{primaryDomain}</span>
                            </li>
                        )}
                        {organizationName.length === 0 ? (
                            <span className="mt-5 font_14 d-flex justify-content-center my-4">No Similar Domains Found</span>
                        ) : (
                            organizationName.map((item) => {
                                // Use the type the API reports when it sends one;
                                // falls back to the previously hardcoded 'domain'
                                // so behaviour is unchanged when it does not.
                                const itemType = item?.type || 'domain';
                                return (
                                    <li className="white_list_data" key={`${itemType}-${item?.id}`}>
                                        <input
                                            type="checkbox"
                                            defaultChecked={item?.is_blacklisted === false}
                                            onChange={(e) =>
                                                handleCheckboxChange(item?.id, itemType, e.target.checked)
                                            }
                                        />
                                        {item?.domain_name}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                )}
            </div>

            {showAddDomainModal && (
                <AddDomainModal
                    show={showAddDomainModal}
                    onHide={() => setShowAddDomainModal(false)}
                    onSuccess={() => {
                        setShowAddDomainModal(false);
                        organization_name();
                    }}
                />
            )}
        </CommonModal>
    );
}

export default WhiteListDomainModal;
